import React, { useState, useCallback, useMemo, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../navigation/Navbar';
import Sidebar from '../navigation/Sidebar';
import { useSmartTradeRefresher } from '../../hooks/useSmartTradeRefresher'; // Updated to use the smart refresher
import { SetupChecklistBanner } from '../common/SetupChecklistBanner';
import { GlobalSyncStatusListener } from '../common/GlobalSyncStatusListener';
import { useMasterLoading } from '../../hooks/useMasterLoading'; // Import master loading hook

// Define a breakpoint (e.g., Tailwind's md breakpoint)
const MEDIUM_SCREEN_BREAKPOINT = 768;

const DashboardLayout = () => {
  // Initialize sidebar state based on window width
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= MEDIUM_SCREEN_BREAKPOINT);

  // Initialize the smart trade refresher (runs automatically based on user activity)
  useSmartTradeRefresher();

  // Get loading state to conditionally show background
  const { shouldShowBlendingSpinner } = useMasterLoading();

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(!isSidebarOpen);
  }, [isSidebarOpen]);

  // Determine if background should be hidden (when BlendingSpinner is active)
  const shouldHideBackground = shouldShowBlendingSpinner();
  
  // For content opacity, we need to be more specific - only reduce opacity during actual loading transitions
  // Not when the system is stuck in an auth state but content is ready
  const { masterLoadingState, isComplete } = useMasterLoading();
  const shouldReduceContentOpacity = shouldHideBackground && 
    masterLoadingState.stage !== 'complete' && 
    !isComplete() &&
    (masterLoadingState.stage === 'css' || masterLoadingState.stage === 'cache');

  // Memoize the layout structure to prevent unnecessary re-renders
  const layoutContent = useMemo(() => (
    <div className={`flex h-screen relative overflow-hidden ${
      shouldHideBackground 
        ? 'bg-transparent' 
        : 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900'
    }`}>
      
      {/* Render the listener - it doesn't output UI */}
      <GlobalSyncStatusListener />
      
      <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Pass toggleSidebar to Navbar - Always render navbar */}
        <Navbar toggleSidebar={toggleSidebar} />
        
        {/* Add the SetupChecklistBanner here, below Navbar, above main content */}
        <SetupChecklistBanner />
        
        {/* Main content with glassmorphism backdrop */}
        <motion.main 
          id="main-content" 
          className="flex-1 overflow-x-hidden overflow-y-auto px-0 pb-6 pt-0 relative overscroll-contain"
          initial={false} // Disable initial animation to prevent layout flash
          animate={{ opacity: shouldReduceContentOpacity ? 0.1 : 1 }} // Only reduce opacity during actual loading transitions
          transition={{ duration: 0.2 }} // Faster transition
          style={{ touchAction: 'pan-y' }}
        >
          <div className="relative z-10">
            {/* Suspense boundary for lazy-loaded pages - keeps navbar/sidebar visible during page loading */}
            <Suspense fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-400">Loading page...</p>
                </div>
              </div>
            }>
              <Outlet />
            </Suspense>
          </div>
        </motion.main>
      </div>
    </div>
  ), [isSidebarOpen, toggleSidebar, shouldHideBackground, shouldReduceContentOpacity]);

  return layoutContent;
};

export default React.memo(DashboardLayout);